(() => {
  const ROOT_SELECTOR = '[data-db-media-showcase]';
  const SLIDE_SELECTOR = '[data-db-media-slide]';
  const VIDEO_SELECTOR = '[data-db-video]';

  function isRendered(video) {
    if (!video) return false;

    const wrapper = video.closest('.db-media-showcase__video-desktop, .db-media-showcase__video-mobile');
    const target = wrapper || video;
    const style = window.getComputedStyle(target);
    const rect = target.getBoundingClientRect();

    return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  }

  function getDeviceVideo(slide) {
    const videos = Array.from(slide.querySelectorAll(VIDEO_SELECTOR));
    return videos.find(isRendered) || videos[0] || null;
  }

  function setPreload(video, value, reload = false) {
    if (!video || video.preload === value) return;

    video.preload = value;
    video.setAttribute('preload', value);

    if (reload && video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
      try {
        video.load();
      } catch (_) {}
    }
  }

  function playAutoplayVideo(video) {
    if (!video || !video.hasAttribute('autoplay')) return;

    // Reinforce the attributes required by modern browser autoplay policies.
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;

    const attemptPlay = () => {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    };

    attemptPlay();

    if (video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
      video.addEventListener('canplay', attemptPlay, { once: true });
    }
  }

  function getActiveIndex(root, track, slides) {
    const activeControl = root.querySelector('[data-db-media-dot][aria-current="true"]');
    if (activeControl) {
      const index = Number(activeControl.dataset.dbMediaDot);
      if (Number.isInteger(index) && index >= 0 && index < slides.length) return index;
    }

    const transform = track.style.transform || '';
    const match = transform.match(/translateX\(\s*(-?[\d.]+)%\s*\)/i);
    if (match) {
      const percent = Math.abs(Number(match[1]));
      const index = Math.round(percent / 100);
      if (index >= 0 && index < slides.length) return index;
    }

    return 0;
  }

  function initRoot(root) {
    if (!root || root.dataset.dbVideoOptimizerInitialized === 'true') return;

    const track = root.querySelector('[data-db-media-track]');
    const slides = Array.from(root.querySelectorAll(SLIDE_SELECTOR));
    if (!track || slides.length === 0) return;

    root.dataset.dbVideoOptimizerInitialized = 'true';

    // Stacked mode intentionally keeps the section's original behavior.
    // The optimization below is for the carousel/single-slide path where only one media item is active.
    if (root.classList.contains('db-media-showcase--stacked')) return;

    let activeIndex = -1;
    let resizeFrame = 0;

    function tuneVideos(nextIndex, force = false) {
      const indexChanged = nextIndex !== activeIndex;
      activeIndex = nextIndex;

      slides.forEach((slide, slideIndex) => {
        const videos = Array.from(slide.querySelectorAll(VIDEO_SELECTOR));
        if (videos.length === 0) return;

        const deviceVideo = getDeviceVideo(slide);
        const distance = Math.abs(slideIndex - activeIndex);
        const isNeighbor = slides.length > 1 && (distance === 1 || distance === slides.length - 1);

        videos.forEach((video) => {
          const isDeviceVideo = video === deviceVideo;
          const isActive = slideIndex === activeIndex && isDeviceVideo;

          if (!isDeviceVideo) {
            video.pause();
            setPreload(video, 'none');
            return;
          }

          if (isActive) {
            const shouldAutoplay = video.hasAttribute('autoplay');
            setPreload(video, shouldAutoplay ? 'auto' : 'metadata', shouldAutoplay && (indexChanged || force));
            if (shouldAutoplay) playAutoplayVideo(video);
            return;
          }

          video.pause();
          setPreload(video, isNeighbor ? 'metadata' : 'none');
        });
      });
    }

    function sync(force = false) {
      tuneVideos(getActiveIndex(root, track, slides), force);
    }

    const trackObserver = new MutationObserver(() => {
      // Existing section JS updates transform/controls synchronously; running in the
      // mutation microtask means we resume the newly active video after it pauses the old one.
      sync();
    });

    trackObserver.observe(track, {
      attributes: true,
      attributeFilter: ['style']
    });

    window.addEventListener('resize', () => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => sync(true));
    }, { passive: true });

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) sync(true);
    });

    // Run once after layout is available, then again after the section's own
    // requestAnimationFrame initialization has set the first transform.
    requestAnimationFrame(() => {
      sync(true);
      requestAnimationFrame(() => sync());
    });
  }

  function initWithin(container = document) {
    if (container.matches && container.matches(ROOT_SELECTOR)) initRoot(container);
    container.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWithin(document), { once: true });
  } else {
    initWithin(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initWithin(event.target);
  });
})();
